/**
 * ModuleReports — reusable report framework for any module.
 * Pass `reports` config array and `apiPrefix` to render a full report suite.
 * Supports CSV & branded PDF export, system-timezone date formatting, and scheduling.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { FileText, ChevronLeft, Download, FileDown, Search, Calendar, Mail, Star, FolderOpen } from 'lucide-react';
import ScheduleReportDialog from './ScheduleReportDialog';
import AdvancedFilterBuilder from '../../components/AdvancedFilterBuilder';
import ColumnPicker from './ColumnPicker';
import { eyvoLogo } from './PDFGenerator';

import { fmtCurrency, fmtNum, fmtDateTz, fmtDateOnlyTz, isDateKey, exportCSV, fmtCurrencyCode, fmtPct } from './reportUtils';

// Re-export utilities so any existing `import { fmtCurrency } from './ModuleReports'` still works
export { fmtCurrency, fmtNum, fmtDateTz, fmtDateOnlyTz, exportCSV, fmtCurrencyCode, fmtPct };
import generateReportPDF from './PDFGenerator';
import EmailReportDialog from './EmailReportDialog';
import ReportTable from './ReportTable';
import { useAuth } from '../../contexts/AuthContext';

// ── Report Framework Component ───────────────────────────────────────

export default function ModuleReports({ reports, apiPrefix, moduleLabel }) {
  const { systemSettings } = useAuth();
  const sysCurrency = systemSettings?.system_currency || 'USD';
  const [selectedReport, setSelectedReport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [lastAdvancedSpec, setLastAdvancedSpec] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [tz, setTz] = useState('America/Los_Angeles');
  const [savedQueries, setSavedQueries] = useState([]);
  const [showLineItems, setShowLineItems] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
  const [reportFormat, setReportFormat] = useState('header_only');
  const [pendingFilter, setPendingFilter] = useState(null);
  const [customCols, setCustomCols] = useState(null); // user's column prefs (visible + ordered)
  const tzLoaded = useRef(false);
  const autoRanRef = useRef(null);

  // Fetch system timezone once
  useEffect(() => {
    if (tzLoaded.current) return;
    tzLoaded.current = true;
    api.get('/api/system/settings').then(r => {
      if (r.data?.timezone) setTz(r.data.timezone);
    }).catch(() => {});
  }, []);

  // Fetch ALL saved reports for this module (for home page & favourites)
  const loadSavedQueries = useCallback(() => {
    api.get('/api/saved-reports').then(r => {
      const all = r.data || [];
      setSavedQueries(all.filter(s => reports.some(rp => rp.advancedModule === s.module)));
    }).catch(() => {});
  }, [reports]);

  useEffect(() => { loadSavedQueries(); }, [loadSavedQueries]);

  const moduleKey = apiPrefix?.split('/').pop() || '';

  // Re-fetch data when showLineItems toggled (only for non-reportFormats reports)
  useEffect(() => {
    if (selectedReport && data) {
      const report = reports.find(r => r.id === selectedReport);
      // Skip re-fetch for reports with reportFormats — they always fetch with include_lines
      if (report?.reportFormats) return;
      if (report?.advancedModule && lastAdvancedSpec) {
        runAdvancedQuery(report.advancedModule, lastAdvancedSpec);
      } else if (!report?.advancedModule) {
        runReport(selectedReport, filters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLineItems]);

  // Auto-run report when selected (eliminates the need to click "Run Report")
  useEffect(() => {
    if (!selectedReport || autoRanRef.current === selectedReport) return;
    autoRanRef.current = selectedReport;
    const report = reports.find(r => r.id === selectedReport);
    if (!report) return;
    if (report.advancedModule) {
      const spec = pendingFilter || { logic: 'AND', conditions: [] };
      runAdvancedQuery(report.advancedModule, spec);
      // pendingFilter is consumed by AdvancedFilterBuilder via prop; clear after use
      if (pendingFilter) setTimeout(() => setPendingFilter(null), 100);
    } else {
      runReport(selectedReport, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  const runReport = useCallback(async (reportId, params = {}) => {
    setLoading(true);
    try {
      const merged = { ...params };
      if (showLineItems) merged.include_lines = 'true';
      const qs = new URLSearchParams(
        Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '' && v !== undefined))
      ).toString();
      const r = await api.get(`${apiPrefix}/${reportId}${qs ? '?' + qs : ''}`);
      setData(r.data);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load report'));
    }
    setLoading(false);
  }, [apiPrefix, showLineItems]);

  // Advanced query for management reports
  const runAdvancedQuery = useCallback(async (advModule, filterSpec) => {
    setLoading(true);
    setLastAdvancedSpec(filterSpec);
    try {
      const report = reports.find(r => r.advancedModule === advModule);
      const needLines = report?.reportFormats ? true : showLineItems;
      const r = await api.post(`/api/saved-reports/query/${advModule}`, { filters: filterSpec, include_lines: needLines });
      setData(r.data);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to run query'));
    }
    setLoading(false);
  }, [showLineItems, reports]);

  // Helper: flatten rows with line items for export — parent data repeats on each line-item row
  const flattenWithLineItems = useCallback((rows, liCols) => {
    if (!rows || !liCols) return rows;
    const result = [];
    for (const row of rows) {
      result.push({ ...row, _isParent: true });
      const items = row.line_items || [];
      for (const li of items) {
        // Carry forward all parent columns so exports show full context per line
        const child = { ...row, _isLineItem: true };
        delete child.line_items;
        delete child._isParent;
        child.li_item_code = li.item_code || li.itemCode || '';
        child.li_item_description = li.item_description || li.description || '';
        child.li_quantity = li.quantity || li.qty || 0;
        child.li_unit_price = li.unit_price || li.unitPrice || 0;
        child.li_net_price = li.net_price || li.netPrice || li.total || 0;
        child.li_account_code = li.account_code || li.accountCode || '';
        child.li_category_code = li.category_code || li.categoryCode || '';
        result.push(child);
      }
    }
    return result;
  }, []);

  // ── Report Home (report cards grid) ───────────────────────────────

  if (!selectedReport) {
    return (
      <div data-testid={`${moduleLabel}-reports-home`}>
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold"><FileText className="h-4 w-4 inline mr-1.5 text-primary" /> {moduleLabel} Reports</h2>
            <p className="text-xs text-muted-foreground mt-0.5">All reports support sorting, filtering, CSV &amp; PDF export.</p>
          </div>
          <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer">
            <img src={eyvoLogo} alt="Eyvo" className="h-8 opacity-90 hover:opacity-100 transition-opacity" />
          </a>
        </div>

        {/* Saved Queries section — all saved queries for this module */}
        {savedQueries.length > 0 && (
          <div className="mb-4 p-3 border rounded-xl bg-slate-50/60 border-slate-200/60" data-testid="saved-queries-section">
            <div className="flex items-center gap-1.5 mb-2">
              <FolderOpen className="h-3.5 w-3.5 text-slate-600" />
              <span className="text-xs font-semibold text-slate-800">Saved Queries</span>
              <span className="text-[10px] text-muted-foreground">— click to run</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {savedQueries.map(s => (
                <button key={s.id} onClick={() => {
                  // Find which report this saved query belongs to
                  const targetReport = reports.find(rp => rp.advancedModule === s.module);
                  if (targetReport) {
                    setPendingFilter(s.filters);
                    autoRanRef.current = null; // reset so auto-run fires
                    setData(null);
                    setSelectedReport(targetReport.id);
                  }
                }}
                  className="text-xs px-3 py-1.5 rounded-lg border bg-white text-slate-800 hover:bg-primary/5 hover:border-primary/30 transition-colors font-medium shadow-sm flex items-center gap-1.5"
                  data-testid={`saved-query-${s.id}`}>
                  {s.starred && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {reports.map(r => (
            <button key={r.id} onClick={() => { autoRanRef.current = null; setSelectedReport(r.id); setData(null); setFilters({}); setPendingFilter(null); }}
              className={`flex items-start gap-3 p-4 border rounded-xl text-left hover:border-primary/30 transition-all group ${r.id === 'management' ? 'hover:bg-slate-50 border-slate-200' : 'hover:bg-accent/20'}`}
              data-testid={`report-card-${r.id}`}>
              <div className={`p-2 rounded-lg ${r.color || 'bg-blue-50'}`}>
                <r.icon className={`h-5 w-5 ${r.iconColor || 'text-blue-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold group-hover:text-primary transition-colors">{r.label}</span>
                  {r.badge && <Badge className={`text-[8px] ${r.badgeColor || 'bg-blue-100 text-blue-700'}`}>{r.badge}</Badge>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{r.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Report View (selected report) ─────────────────────────────────

  const report = reports.find(r => r.id === selectedReport);
  const isAdvanced = report?.advancedModule;
  const cols = report?.columns || (data?.rows?.[0]
    ? Object.keys(data.rows[0]).filter(k => !Array.isArray(data.rows[0][k]) && typeof data.rows[0][k] !== 'object')
        .map(k => ({
          key: k,
          label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          align: (typeof data.rows[0][k] === 'number') ? 'right' : 'left',
        }))
    : []);

  // Determine default columns for column picker
  const defaultPickerCols = (() => {
    if (report?.reportFormats) {
      const fmt = report.reportFormats.find(f => f.value === reportFormat);
      return fmt?.columns || [];
    }
    return cols;
  })();

  // Effective columns used for rendering (customCols overrides defaults)
  const hasAddressCols = cols.some(c => c.addressCol);
  const effectiveCols = (customCols || cols).filter(c => showAddresses || !c.addressCol);

  const handlePDF = () => {
    if (!data?.rows?.length) { toast.error('No data to export'); return; }
    const activeFormat = report?.reportFormats?.find(f => f.value === reportFormat);
    if (activeFormat) {
      // Apply customCols to format columns if user has customized
      const fmtWithCustom = customCols ? { ...activeFormat, columns: customCols } : activeFormat;
      const doc = generateReportPDF(
        `${moduleLabel} — ${report?.label || selectedReport}`,
        null, data.rows, data.summary, tz,
        { reportFormat: fmtWithCustom, moduleLabel }
      );
      doc.save(`${selectedReport}_${reportFormat}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Exported to PDF');
      return;
    }
    const exportRows = showLineItems && report?.lineItemColumns
      ? flattenWithLineItems(data.rows, report.lineItemColumns) : data.rows;
    const exportCols = showLineItems && report?.lineItemColumns
      ? [...effectiveCols, ...report.lineItemColumns] : effectiveCols;
    const doc = generateReportPDF(`${moduleLabel} — ${report?.label || selectedReport}`, exportCols, exportRows, data.summary, tz);
    doc.save(`${selectedReport}_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('Exported to PDF');
  };

  const handleEmailPdf = () => {
    if (!data?.rows?.length) return null;
    const activeFormat = report?.reportFormats?.find(f => f.value === reportFormat);
    if (activeFormat) {
      const fmtWithCustom = customCols ? { ...activeFormat, columns: customCols } : activeFormat;
      return generateReportPDF(
        `${moduleLabel} — ${report?.label || selectedReport}`,
        null, data.rows, data.summary, tz,
        { reportFormat: fmtWithCustom, moduleLabel }
      );
    }
    const exportRows = showLineItems && report?.lineItemColumns
      ? flattenWithLineItems(data.rows, report.lineItemColumns) : data.rows;
    const exportCols = showLineItems && report?.lineItemColumns
      ? [...effectiveCols, ...report.lineItemColumns] : effectiveCols;
    return generateReportPDF(`${moduleLabel} — ${report?.label || selectedReport}`, exportCols, exportRows, data.summary, tz);
  };

  const handleCSV = () => {
    const activeFormat = report?.reportFormats?.find(f => f.value === reportFormat);
    if (activeFormat) {
      const fmtWithCustom = customCols ? { ...activeFormat, columns: customCols } : activeFormat;
      const csvRows = buildFormatCSVRows(data.rows, fmtWithCustom);
      exportCSV(csvRows, `${selectedReport}_${reportFormat}`);
      return;
    }
    const exportRows = showLineItems && report?.lineItemColumns
      ? flattenWithLineItems(data.rows, report.lineItemColumns) : data.rows;
    // For Data Files and Account Codes reports, export ALL row fields (comprehensive CSV)
    const isDataFileReport = report?.badge === 'Master Data' || report?.badge === 'Account Codes';
    if (isDataFileReport) {
      // Collect all unique keys from rows, use readable headers
      const allKeys = new Set();
      exportRows.forEach(r => Object.keys(r).forEach(k => {
        if (k !== '_id' && k !== '__v' && k !== '_version') allKeys.add(k);
      }));
      const orderedKeys = [...allKeys];
      const csvData = exportRows.map(r => {
        const obj = {};
        orderedKeys.forEach(k => {
          const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          const val = r[k];
          obj[label] = val !== undefined && val !== null ? (typeof val === 'object' ? JSON.stringify(val) : val) : '';
        });
        return obj;
      });
      exportCSV(csvData, `${selectedReport}`);
      return;
    }
    // When customCols is set, build column-filtered CSV
    if (customCols && customCols.length) {
      const csvCols = showLineItems && report?.lineItemColumns ? [...customCols, ...report.lineItemColumns] : customCols;
      const filtered = exportRows.map(r => {
        const obj = {};
        csvCols.forEach(c => { obj[c.label] = r[c.key] !== undefined ? r[c.key] : ''; });
        return obj;
      });
      exportCSV(filtered, `${selectedReport}`);
    } else {
      exportCSV(exportRows, `${selectedReport}`);
    }
  };

  /** Build CSV-ready rows for reportFormat modes — generic for all modules */
  const buildFormatCSVRows = (rows, fmt) => {
    const lf = fmt.linesField || 'line_items';
    const getV = (row, key) => {
      if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
      const fb = { delivery_point_code: ['delivery_code'], document_type_name: ['document_type'], invoice_point_name: ['invoice_code'], order_date: ['created_at'], issued_date: ['issue_date'], entry_date: ['created_at'] };
      for (const alt of (fb[key] || [])) { if (row[alt] !== undefined && row[alt] !== null && row[alt] !== '') return row[alt]; }
      return '';
    };
    if (fmt.value === 'header_only') {
      return rows.map(r => {
        const obj = {};
        for (const c of fmt.columns) {
          if (c.key === '_line_count') obj[c.label] = (r[lf] || []).length;
          else if (c.key === '_doc_value') {
            const v = (fmt.valueFields || []).reduce((val, f) => val || r[f], null);
            obj[c.label] = `${parseFloat(v || 0).toFixed(2)} (${r.currency || 'USD'})`;
          } else obj[c.label] = getV(r, c.key);
        }
        return obj;
      });
    }
    if (fmt.value === 'line_only') {
      const result = [];
      const parentFields = fmt.parentFields || [];
      for (const r of rows) {
        for (const li of (r[lf] || [])) {
          const obj = {};
          for (const c of fmt.columns) {
            if (parentFields.includes(c.key)) obj[c.label] = r[c.key] || '';
            else if (c.pct || c.key === 'discount_pct') obj[c.label] = `${Number(li.discount || 0).toFixed(2)}%`;
            else if (c.currency) obj[c.label] = `${Number(li[c.key] || 0).toFixed(2)} (${r.currency || 'USD'})`;
            else if (c.qty) obj[c.label] = Number(li[c.key] || 0).toFixed(2);
            else obj[c.label] = li[c.key] !== undefined ? li[c.key] : '';
          }
          result.push(obj);
        }
      }
      return result;
    }
    // combo — export as header + lines interleaved
    const pkField = fmt.primaryKeyField || 'order_number';
    const result = [];
    for (const r of rows) {
      const header = { _type: 'header' };
      header[pkField] = r[pkField]; header.status = r.status;
      if (fmt.valueFields) header.doc_value = (fmt.valueFields || []).reduce((val, f) => val || r[f], 0);
      result.push(header);
      for (const li of (r[lf] || [])) {
        const line = { _type: 'line' };
        for (const c of (fmt.lineColumns || [])) line[c.label] = li[c.key];
        result.push(line);
      }
    }
    return result;
  };

  // Export bar JSX - rendered inline to preserve ColumnPicker state
  const exportBarJSX = data?.rows?.length > 0 ? (
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      {report?.reportFormats && (
        <div className="flex items-center gap-1.5 mr-2" data-testid="report-format-selector">
          <span className="text-xs font-semibold text-muted-foreground">Format:</span>
          <select value={reportFormat} onChange={e => { setReportFormat(e.target.value); setCustomCols(null); }}
            className="h-7 text-xs border rounded px-2 bg-white font-medium" data-testid="report-format-dropdown">
            {report.reportFormats.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      )}
      <Button size="sm" variant="outline" onClick={handleCSV} data-testid="export-csv-btn">
        <Download className="h-3.5 w-3.5 mr-1" /> CSV
      </Button>
      <Button size="sm" variant="outline" onClick={handlePDF} data-testid="export-pdf-btn"
        className="border-red-200 text-red-700 hover:bg-red-50">
        <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
      </Button>
      <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} data-testid="email-report-btn"
        className="border-blue-200 text-blue-700 hover:bg-blue-50">
        <Mail className="h-3.5 w-3.5 mr-1" /> Email
      </Button>
      <Button size="sm" variant="outline" onClick={() => setScheduleOpen(true)} data-testid="schedule-this-report-btn">
        <Calendar className="h-3.5 w-3.5 mr-1" /> Schedule
      </Button>
      {!report?.reportFormats && report?.lineItemColumns && (
        <label className="flex items-center gap-1.5 ml-2 cursor-pointer select-none" data-testid="show-line-items-toggle">
          <input type="checkbox" checked={showLineItems} onChange={e => setShowLineItems(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary" />
          <span className="text-xs font-medium text-muted-foreground">Show Line Items</span>
        </label>
      )}
      {hasAddressCols && (
        <label className="flex items-center gap-1.5 ml-2 cursor-pointer select-none" data-testid="show-addresses-toggle">
          <input type="checkbox" checked={showAddresses} onChange={e => setShowAddresses(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary" />
          <span className="text-xs font-medium text-muted-foreground">Show Addresses</span>
        </label>
      )}
      {defaultPickerCols?.length > 0 && (
        <ColumnPicker
          module={moduleKey}
          reportId={selectedReport}
          formatValue={report?.reportFormats ? reportFormat : null}
          defaultColumns={defaultPickerCols}
          onColumnsChange={setCustomCols}
        />
      )}
    </div>
  ) : null;

  return (
    <div data-testid={`report-view-${selectedReport}`}>
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" variant="ghost" onClick={() => { setSelectedReport(null); setData(null); }} data-testid="report-back">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-base font-bold">{report && <report.icon className="h-4 w-4 inline mr-1.5 text-primary" />}{report?.label}</h2>
          <p className="text-xs text-muted-foreground">{report?.desc}</p>
        </div>
        <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer">
          <img src={eyvoLogo} alt="Eyvo" className="h-8 opacity-90 hover:opacity-100 transition-opacity" />
        </a>
      </div>

      {/* ── Advanced filter builder (management reports) ── */}
      {isAdvanced && (
        <>
          <AdvancedFilterBuilder
            module={report.advancedModule}
            reportId={selectedReport}
            onRunQuery={(spec) => runAdvancedQuery(report.advancedModule, spec)}
            loading={loading}
            initialFilter={pendingFilter}
          />
          {exportBarJSX}
        </>
      )}

      {/* ── Basic filter bar (standard reports) ── */}
      {!isAdvanced && report?.filters && (
        <div className="flex items-end gap-3 mb-4 p-3 bg-muted/30 rounded-xl border flex-wrap">
          {report.filters.map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-[10px] font-semibold">{f.label}</Label>
              {f.type === 'select' ? (
                <select value={filters[f.key] || ''} onChange={e => setFilters(p => ({ ...p, [f.key]: e.target.value }))}
                  className="h-7 text-xs border rounded px-2 w-36 bg-white" data-testid={`filter-${f.key}`}>
                  <option value="">All</option>
                  {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <Input type={f.type || 'text'} value={filters[f.key] || ''}
                  onChange={e => setFilters(p => ({ ...p, [f.key]: e.target.value }))}
                  className="h-7 text-xs w-36" placeholder={f.placeholder || f.label}
                  data-testid={`filter-${f.key}`} />
              )}
            </div>
          ))}
          <Button size="sm" onClick={() => runReport(selectedReport, filters)} disabled={loading} data-testid="run-report-btn">
            {loading ? 'Loading...' : 'Re-Run'}
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {exportBarJSX}
          </div>
        </div>
      )}

      {/* Export bar for no-filter standard reports (auto-run handles fetching) */}
      {!isAdvanced && !report?.filters && exportBarJSX}

      {loading && <div className="text-center py-8 text-muted-foreground text-sm">Loading report data...</div>}

      {data && !loading && (
        <>
          {/* Summary cards */}
          {data.summary && (
            <div className="flex gap-3 flex-wrap mb-4 items-end">
              {Object.entries(data.summary).filter(([k]) => k !== 'buckets' && k !== 'rate_source' && k !== 'last_updated').map(([k, v]) => (
                <div key={k} className="p-3 border rounded-lg bg-white min-w-[120px]">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{k.replace(/_/g, ' ')}</p>
                  <p className="text-lg font-bold">{typeof v === 'number' && (k.includes('value') || k.includes('cost') || k.includes('nbv') || k.includes('paid') || k.includes('outstanding') || k.includes('payable') || k.includes('proceeds') || k.includes('savings') || k.includes('awarded') || k.includes('depreciation'))
                    ? fmtCurrency(v) : fmtNum(v)}</p>
                </div>
              ))}
              {data.summary.rate_source && (
                <div className="p-3 border rounded-lg bg-blue-50/60 border-blue-200 min-w-[200px]">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Rate Source</p>
                  <p className="text-sm font-semibold text-blue-800">{data.summary.rate_source}</p>
                  {data.summary.last_updated && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Last updated: {fmtDateTz(data.summary.last_updated, tz)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Data table */}
          {data.rows && <ReportTable
            rows={data.rows} columns={effectiveCols} tz={tz}
            drillDown={report?.drillDown}
            lineItemColumns={report?.lineItemColumns}
            showLineItems={showLineItems}
            reportFormatConfig={report?.reportFormats ? report.reportFormats.find(f => f.value === reportFormat) : null}
            customCols={customCols}
            systemCurrency={sysCurrency}
          />}
        </>
      )}

      <div className="mt-6 pt-4 border-t border-muted flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground">&copy; {new Date().getFullYear()} Eyvo. All rights reserved.</p>
        <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 text-[10px] text-primary/70 hover:text-primary transition-colors font-medium">
          Powered by Eyvo
        </a>
      </div>

      <ScheduleReportDialog
        open={scheduleOpen} onClose={() => setScheduleOpen(false)}
        prefill={{ module: moduleKey, report_id: selectedReport, report_label: report?.label || '', module_label: moduleLabel || '', filters: { ...filters } }}
      />
      <EmailReportDialog open={emailOpen} onClose={() => setEmailOpen(false)} onGeneratePdf={handleEmailPdf} reportLabel={`${moduleLabel} — ${report?.label || ''}`} />
    </div>
  );
}
